import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { callStatsDataSource, Resolved } from './dataSource';
import {
  CallStatsAction,
  CallStatsActionTypes,
  CallStatsFilters,
  CallsQuery,
  PanelKey,
  QualityDimension,
} from './callStatsTypes';

export const setFilters = (
  filters: Partial<CallStatsFilters>,
): CallStatsAction => ({
  type: CallStatsActionTypes.SET_FILTERS,
  payload: filters,
});

const request = (key: PanelKey): CallStatsAction => ({
  type: CallStatsActionTypes.PANEL_REQUEST,
  payload: { key },
});

const success = (
  key: PanelKey,
  data: unknown,
  mocked: boolean,
): CallStatsAction => ({
  type: CallStatsActionTypes.PANEL_SUCCESS,
  payload: { key, data, mocked },
});

const failure = (key: PanelKey, error: string): CallStatsAction => ({
  type: CallStatsActionTypes.PANEL_FAILURE,
  payload: { key, error },
});

function runPanel<T>(
  key: PanelKey,
  exec: () => Promise<Resolved<T>>,
  toastOnError = true,
) {
  return async (dispatch: Dispatch<CallStatsAction>) => {
    dispatch(request(key));
    try {
      const { data, mocked } = await exec();
      dispatch(success(key, data, mocked));
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to load call stats';
      dispatch(failure(key, msg));
      // callDetail surfaces its own in-dialog empty/error state; a global toast on
      // the common no-quality-data 404 (a normal row click) would be noise.
      if (toastOnError) toast.error(msg);
    }
  };
}

export const fetchSummary = (f: CallStatsFilters) =>
  runPanel('summary', () => callStatsDataSource.getSummary(f));

export const fetchByUser = (f: CallStatsFilters) =>
  runPanel('byUser', () => callStatsDataSource.getByUser(f, 10));

export const fetchPair = (f: CallStatsFilters) =>
  runPanel('pair', () => callStatsDataSource.getPair(f));

export const fetchFailures = (f: CallStatsFilters) =>
  runPanel('failures', () => callStatsDataSource.getFailures(f));

export const fetchConnectivity = (f: CallStatsFilters) =>
  runPanel('connectivity', () => callStatsDataSource.getConnectivity(f));

export const fetchScores = (f: CallStatsFilters) =>
  runPanel('scores', () => callStatsDataSource.getScores(f));

export const fetchByDimension = (
  f: CallStatsFilters,
  dimension: QualityDimension,
) =>
  runPanel('byDimension', () =>
    callStatsDataSource.getByDimension(f, dimension),
  );

export const fetchCalls = (q: CallsQuery) =>
  runPanel('calls', () => callStatsDataSource.getCalls(q));

export const fetchCallDetail = (callId: string) =>
  runPanel(
    'callDetail',
    () => callStatsDataSource.getCallDetail(callId),
    false,
  );
